<template>
    <div class="landing-page-learn">
        <div class="text-center">
            <h1 class="main-title font-weight-bold">
                Market Insights from Local Experts
            </h1>

            <p class="sub-title mb-0">
                Knowledge to guide your real estate transactions
            </p>
            <search-field
                    v-model="tableFetch.filterForm.title"
                    @input="filterChange"
                    has-button
            />

            <div
                    v-if="!appResourcesSearch.loading && !learnSectionLoading && !knowledgeAssets.length"
                    class="text-center font-size-lg text-gray-600 bold mx-auto mt-5"
            >
                No assets found
            </div>
            <div class="cards-row">
                <div class="cards-row__wrapper d-flex flex-wrap justify-content-center">
                    <template v-for="(item, index) in knowledgeAssets">
                        <knowledge-asset-card
                                :key="index"
                                :asset="item"
                                @click="handleKnowledgeAssetClick(item)"
                        />
                    </template>
                </div>
            </div>
            <template v-if="showLoader">
                <div
                        class="cards-row"
                        v-b-visible="handleScroll"
                >
                    <div class="cards-row__wrapper d-flex flex-wrap justify-content-center">
                        <template v-for="item in 4">
                            <div
                                    class="knowledge-asset-card-loader"
                                    :key="item"
                            >
                                <b-skeleton-img
                                        no-aspect
                                        height="404px"
                                        width="255px"
                                />
                            </div>
                        </template>
                    </div>
                </div>
            </template>
        </div>
        <knowledge-asset-modal />
    </div>
</template>

<script>
    import './Learn.scss';
    import LandingPageAppResource from "@milestoneslabs/milestones-core-js/src/AppResources/LandingPageAppResource";
    import SearchField from "../../../components/SearchField/SearchField";
    import AppResourceSearch from "@milestoneslabs/milestones-js-engine/src/Data/AppResourceSearch";
    import isEmpty from "lodash/isEmpty";
    import forEach from "lodash/forEach";
    import ApiFilter from "@milestoneslabs/milestones-js-engine/src/Api/ApiFilter";
    import KnowledgeAssetCard from "../../../components/LandingPage/Learn/KnowledgeAssetCard/KnowledgeAssetCard";
    import ShowNotificationMixin from "@milestoneslabs/milestones-ui-kit/src/mixins/ShowNotificationMixin";
    import KnowledgeAssetModal,
    {KNOWLEDGE_ASSET_MODAL_SHOW}
        from "../../../components/LandingPage/Learn/KnowledgeAssetModal/KnowledgeAssetModal";

    export default {
        name: "Learn",
        components: {
            KnowledgeAssetModal,
            KnowledgeAssetCard,
            SearchField
        },
        mixins: [
            ShowNotificationMixin
        ],
        props: {
            landingPage: {
                type: LandingPageAppResource,
                required: true,
            },
        },
        data() {
            return {
                landingPageUuid: window.landingPageUuid,
                learnSectionLoading: false,
                appResourcesSearch: new AppResourceSearch('Knowledge-Asset'),
                tableFetch: {
                    pageSize: 8,
                    include: ['files', 'tags'],
                    filterForm: {
                        page: 1,
                        title: '',
                        isPublished: true,
                        knowledgeBaseUuid: null
                    },
                    loading: true,
                },
                knowledgeAssets: [],
                isLoaderVisible: false,
            };
        },
        computed: {
            searchData() {
                const searchData = {
                    include: this.tableFetch.include,
                    pageSize: this.tableFetch.pageSize,
                    page: this.tableFetch.filterForm.page,
                    orderBy: this.tableFetch.orderBy,
                };
                const appResource = this.appResourcesSearch.coreAppResource;
                const apiFilter = this.buildSearchFilter(appResource, this.tableFetch.filterForm);
                if (apiFilter !== null && apiFilter.hasData()) {
                    searchData.filter = apiFilter;
                }
                return searchData;
            },
            meta() {
                return !isEmpty(this.appResourcesSearch.meta) ? this.appResourcesSearch.meta : {};
            },
            total() {
                return this.appResourcesSearch.total ? this.appResourcesSearch.total : this.appResourcesSearch.length;
            },

            isLoading() {
                return this.appResourcesSearch.loading;
            },
            showLoader() {
                return this.appResourcesSearch.loading ||
                    (
                        this.appResourcesSearch.meta &&
                        this.appResourcesSearch.lastPage &&
                        this.tableFetch.filterForm.page < this.appResourcesSearch.lastPage
                    );
            }
        },
        methods: {
            async fetchLearnSection() {
                if (!this.landingPageUuid) {
                    return;
                }

                this.learnSectionLoading = true;
                let search = new AppResourceSearch('landing-page-learn-section');

                search.setSearchData({
                    filter: new ApiFilter({
                        params: {
                            landingPageUuid: this.landingPageUuid
                        }
                    })
                });

                try {
                    await search.search();

                    if (search.appResourceCollection.isNotEmpty) {
                        this.tableFetch.filterForm.knowledgeBaseUuid = search.appResourceCollection
                            .getFirst()
                            .knowledgeBaseUuid;
                    }

                } catch (e) {
                    console.warn(e);
                    this.showNotification({
                        message: `Something went wrong while fetching finance section! Please try again later!`,
                        type: 'error',
                    });
                } finally {
                    this.learnSectionLoading = false;
                }
            },
            buildSearchFilter(appResource, formData) {

                if (isEmpty(formData)) {
                    return null;
                }

                const filter = new ApiFilter({});
                forEach(formData, (formItem, formKey) => {
                    if (typeof formItem !== 'boolean' && isEmpty(formItem)) {
                        return true;
                    }

                    if (appResource.relationMap().hasOwnProperty(formKey)) {
                        const RelationAppResource = appResource.relationMap()[formKey];
                        const resourceFilter = this.buildSearchFilter(new RelationAppResource(), formItem);
                        if (filter !== null) {
                            if (resourceFilter.isValid) {
                                filter.addRelation(formKey, resourceFilter);
                            }
                        }
                        return true;
                    }
                    if (
                        appResource.fillable().indexOf(formKey) < 0 &&
                        appResource.hidden().indexOf(formKey) < 0
                    ) {

                        return true;
                    }

                    filter.addParam(formKey, formItem);

                });

                return filter;
            },
            loadData() {
                const searchData = this.searchData;
                this.appResourcesSearch.setSearchData(searchData, false);
            },
            async filterChange() {
                this.tableFetch.filterForm.page = 1;
                this.loadData();
                this.knowledgeAssets = [];
                await this.fetchKnowledgeAssets();
            },
            async fetchKnowledgeAssets() {
                this.loadData();
                try {
                    await this.appResourcesSearch.search();
                    this.knowledgeAssets.push(...this.appResourcesSearch.appResourceCollection.toArray());
                } catch (e) {
                    console.warn(e);
                }

            },
            async handleScroll(isVisible) {
                this.isLoaderVisible = isVisible;

                if (!isVisible) {
                    return;
                }
                if (this.appResourcesSearch.loading) {
                    return;
                }
                if (this.tableFetch.filterForm.page >= this.appResourcesSearch.meta.lastPage) {
                    return;
                }
                this.tableFetch.filterForm.page++;
                await this.fetchKnowledgeAssets();
            },
            handleKnowledgeAssetClick(item) {
                this.$bus.$emit(KNOWLEDGE_ASSET_MODAL_SHOW, {entity: item});
            }
        },

        async beforeMount() {
            await this.fetchLearnSection();
            if (this.tableFetch.filterForm.knowledgeBaseUuid) {
                await this.fetchKnowledgeAssets();
            }

            if (this.isLoaderVisible) {
                await this.handleScroll(this.isLoaderVisible);
            }
        }
    };
</script>
